import Head from 'next/head';
import { useRouter } from 'next/router';

type StructuredDataProps = {
  includeFaq?: boolean;
};

const StructuredData = ({ includeFaq = false }: StructuredDataProps) => {
  const router = useRouter();
  const isFrench = router.asPath.startsWith('/fr');

  const structuredData = {
    en: {
      softwareApp: {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Featcal",
        applicationCategory: "HealthApplication",
        operatingSystem: "iOS, Android, Web",
        description: "Software for weight-loss clinics and metabolic-health teams to manage patient follow-up between visits with tracking, AI-assisted food-log review, adherence nudges, and clinician-ready summaries.",
        url: "https://featcal.com",
        offers: {
          "@type": "Offer",
          category: "Healthcare Software",
          businessFunction: "http://purl.org/goodrelations/v1#Sell",
        },
        audience: {
          "@type": "Audience",
          name: "Healthcare Professionals, Nutritionists, Coaches",
        },
        featureList: [
          "Configurable patient tracking",
          "AI-assisted food log review",
          "Nutrition plans and next steps",
          "Adherence reminders and nudges",
          "Consultation summaries",
          "Program analytics and reporting",
          "Medical device integration",
          "PWA mobile application",
        ],
        screenshot: "/images/customer-admin.webp",
      },
      organization: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Featcal",
        url: "https://featcal.com",
        logo: "https://featcal.com/images/logo.png",
        description: "Healthcare technology company building workflow software for weight-loss and metabolic-health programs.",
        foundingDate: "2024",
        industry: "Healthcare Technology",
        sameAs: [
          "https://x.com/featcal_com",
          "https://www.instagram.com/featcal/",
        ],
        address: {
          "@type": "PostalAddress",
          addressCountry: "US",
        },
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "Customer Service",
          email: "support@featcal.com",
        },
      },
      faq: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What's the ROI of using Featcal?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Featcal centralizes the operational workflow between appointments: patient tracking, food log review, plans, reminders, and summaries. Teams spend less time chasing scattered updates and more time reviewing usable data.",
            },
          },
          {
            "@type": "Question",
            name: "Can I try Featcal before committing?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. Teams can start with a live walkthrough and a pilot-friendly 30-day trial using their real follow-up workflow.",
            },
          },
          {
            "@type": "Question",
            name: "Is Featcal for clinics or private practice?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Featcal works for solo practitioners, clinics, and care teams. A solo practitioner can structure follow-up for each client, while a clinic can standardize tracking, plans, and review across cohorts and team members.",
            },
          },
          {
            "@type": "Question",
            name: "How much does Featcal cost?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Pricing scales with active users so teams can start with a small pilot, prove the workflow, and expand to larger programs and cohorts.",
            },
          },
          {
            "@type": "Question",
            name: "Is my data and my clients' data secure with Featcal?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. Featcal is designed for sensitive health and wellness data, with encrypted data, access-controlled workflows, no selling of patient data, and ongoing security updates.",
            },
          },
        ],
      },
      website: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Featcal",
        url: "https://featcal.com",
        description: "Operating system for weight-loss and metabolic-health programs: patient tracking, AI-assisted food-log review, adherence nudges, and clinician-ready summaries.",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://featcal.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
        publisher: {
          "@type": "Organization",
          name: "Featcal",
          logo: "https://featcal.com/images/logo.png",
        },
      },
    },
    fr: {
      softwareApp: {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Featcal",
        applicationCategory: "HealthApplication",
        operatingSystem: "iOS, Android, Web",
        description: "Logiciel pour cliniques de perte de poids et équipes de santé métabolique afin de gérer le suivi patient entre visites avec tracking, revue IA des journaux, rappels d’adhérence et synthèses cliniques.",
        url: "https://featcal.com",
        offers: {
          "@type": "Offer",
          category: "Logiciel de Santé",
          businessFunction: "http://purl.org/goodrelations/v1#Sell",
        },
        audience: {
          "@type": "Audience",
          name: "Professionnels de Santé, Nutritionnistes, Coachs",
        },
        featureList: [
          "Tracking patient configurable",
          "Revue des journaux alimentaires assistée par IA",
          "Plans nutritionnels et prochaines étapes",
          "Rappels et nudges d’adhérence",
          "Synthèses de consultation",
          "Analytiques et rapports de programme",
          "Intégration d'appareils médicaux",
          "Application mobile PWA",
        ],
        screenshot: "/images/customer-admin.webp",
      },
      organization: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Featcal",
        url: "https://featcal.com",
        logo: "https://featcal.com/images/logo.png",
        description: "Entreprise de technologie de santé construisant un logiciel de workflow pour les programmes de perte de poids et santé métabolique.",
        foundingDate: "2024",
        industry: "Technologie de Santé",
        sameAs: [
          "https://x.com/featcal_com",
          "https://www.instagram.com/featcal/",
        ],
        address: {
          "@type": "PostalAddress",
          addressCountry: "US",
        },
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "Service Client",
          email: "support@featcal.com",
        },
      },
      faq: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Quel est le retour sur investissement de Featcal?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Featcal centralise le workflow opérationnel entre les rendez-vous : tracking patient, revue des journaux alimentaires, plans, rappels et synthèses. Les équipes passent moins de temps à relancer des données dispersées et plus de temps à revoir des informations utilisables.",
            },
          },
          {
            "@type": "Question",
            name: "Puis-je essayer Featcal avant de m'engager?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Oui. Les équipes peuvent commencer avec une démo en direct et un essai de 30 jours adapté à un vrai workflow de suivi.",
            },
          },
          {
            "@type": "Question",
            name: "Featcal est-il pour les cliniques ou les cabinets privés?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Featcal fonctionne pour les praticiens indépendants, les cliniques et les équipes de soins. Un praticien seul peut structurer le suivi de chaque client, tandis qu’une clinique peut standardiser tracking, plans et revue à travers des cohortes et plusieurs membres d’équipe.",
            },
          },
          {
            "@type": "Question",
            name: "Combien coûte Featcal?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Le prix s’adapte aux utilisateurs actifs pour commencer avec un petit pilote, prouver le workflow, puis déployer sur des programmes et cohortes plus larges.",
            },
          },
          {
            "@type": "Question",
            name: "Mes données et celles de mes clients sont-elles sécurisées avec Featcal?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Oui. Featcal est conçu pour des données sensibles de santé et bien-être, avec données chiffrées, accès contrôlés, aucune vente de données patient et mises à jour de sécurité continues.",
            },
          },
        ],
      },
      website: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Featcal",
        url: "https://featcal.com",
        description: "Système d’exploitation des programmes de perte de poids et santé métabolique : tracking patient, revue IA des journaux, rappels d’adhérence et synthèses cliniques.",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://featcal.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
        publisher: {
          "@type": "Organization",
          name: "Featcal",
          logo: "https://featcal.com/images/logo.png",
        },
      },
    },
  };

  const currentData = isFrench ? structuredData.fr : structuredData.en;

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(currentData.softwareApp),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(currentData.organization),
        }}
      />
      {includeFaq && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(currentData.faq),
          }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(currentData.website),
        }}
      />
    </Head>
  );
};

export default StructuredData;
